import { PermissionAccessProvider } from '../../../contexts/PermissionAccessContext';
import type { UnitDetail } from '../../../types/safetySystem';
import { newChildRow } from './ticketForm';
import { CHILD_FIELDS, gridOptions, isCurrentRow, lookupLabel, lookupOptions } from './unitForm';
import { DateField, LookupSelectField, MaskedSsnField, MoneyField, Section, SelectField, TextField, YesNoField, str } from './unitFields';
import { ChildListsProps, ChildrenStatus, UnitChildGrid } from './UnitChildGrid';

/**
 * Truck tab of the Unit drawer — three columns mirroring the V1 Safety truck page.
 * Col 1: owner picker + owner record + Overweight Permits grid (`owpermit`).
 * Col 2: lienholder, inspection dates, IFTA (hidden for mule drivers), ELD grid (`eldinfo`,
 *        the device in use today highlighted).
 * Col 3: truck photo + truck fields.
 *
 * Owner switch: picking another owner sends a top-level `ownerrecno` on Save; the owner
 * fields then belong to the OLD record, so they lock until the switch is saved and the
 * drawer reloads with the new owner's row.
 */

export interface UnitTruckTabProps extends ChildListsProps {
  detail: UnitDetail;
  driver: Record<string, any>;
  truck: Record<string, any>;
  owner: Record<string, any>;
  /** Pending owner pick (recno string); '' / original recno = no switch. */
  ownerrecno: string;
  onTruckChange: (key: string, value: unknown) => void;
  onOwnerChange: (key: string, value: unknown) => void;
  onOwnerRecnoChange: (value: string) => void;
  canEdit: boolean;
  /** `safety-system-payroll-sensitive` >= 2 — shows the owner SSN / Tax ID Edit control. */
  canSensitive: boolean;
  ownerSsn: string | null;
  onOwnerSsnChange: (value: string | null) => void;
  photoUrl: string | null;
  portalContainer: HTMLElement | null;
}

export default function UnitTruckTab({
  detail, driver, truck, owner, ownerrecno, onTruckChange, onOwnerChange, onOwnerRecnoChange,
  canEdit, canSensitive, ownerSsn, onOwnerSsnChange, photoUrl, portalContainer,
  childRows, onChildRowsChange, lookups, childrenLoading, childrenError, rowErrors,
}: UnitTruckTabProps) {
  const L = detail.lookups;
  const gridsReady = !childrenLoading && !childrenError;
  const t = (key: string) => (v: unknown) => onTruckChange(key, v);
  const o = (key: string) => (v: unknown) => onOwnerChange(key, v);
  const isMule = str(driver.muledriver).toUpperCase() === 'Y';
  const hasOwner = !!detail.owner?.recno;
  const ownerSwitched = ownerrecno !== '' && ownerrecno !== str(detail.owner?.recno);
  // Owner fields are editable only for the owner currently linked to the unit.
  const ownerLocked = ownerSwitched || !hasOwner;

  const onLienholder = (v: string | number) => {
    onTruckChange('lienholderrecno', v);
    onTruckChange('lienholder', lookupLabel(L.lienholders, v)); // '' when cleared (0 matches no row)
  };

  return (
    <PermissionAccessProvider accessLevel={canEdit ? 2 : 1}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {/* ---- Column 1: owner ---- */}
        <div className="space-y-3">
          <Section title="Owner">
            <SelectField label="Owner" value={ownerrecno || str(detail.owner?.recno)} onChange={onOwnerRecnoChange} options={lookupOptions(L.owners)} search className="col-span-2" />
            {ownerSwitched && (
              <p className="col-span-2 rounded-md bg-highlight px-2 py-1 text-xs text-muted">
                Save to switch this unit to the selected owner; the owner fields below reload afterwards.
              </p>
            )}
            {!hasOwner && !ownerSwitched && (
              <p className="col-span-2 rounded-md bg-highlight px-2 py-1 text-xs text-muted">No owner linked to this unit.</p>
            )}
            <TextField label="Owner Identifier" value={owner.truckowneridentifier} onChange={o('truckowneridentifier')} readOnly={ownerLocked} />
            <MaskedSsnField label="SSN / Tax ID" last4={owner.truckownerssn_last4} value={ownerSsn} onChange={onOwnerSsnChange} canEdit={canSensitive && canEdit && !ownerLocked} />
            <TextField label="Owner Name" value={owner.truckownername} onChange={o('truckownername')} readOnly={ownerLocked} className="col-span-2" />
            <TextField label="DBA" value={owner.truckownerdba} onChange={o('truckownerdba')} readOnly={ownerLocked} className="col-span-2" />
            <TextField label="Owner Address 1" value={owner.truckowneradd1} onChange={o('truckowneradd1')} readOnly={ownerLocked} className="col-span-2" />
            <TextField label="Owner Address 2" value={owner.truckowneradd2} onChange={o('truckowneradd2')} readOnly={ownerLocked} className="col-span-2" />
            <TextField label="Owner City" value={owner.truckownercity} onChange={o('truckownercity')} readOnly={ownerLocked} className="col-span-2" />
            <TextField label="Owner State" value={owner.truckownerstate} onChange={o('truckownerstate')} readOnly={ownerLocked} maxLength={2} />
            <TextField label="Owner Zip" value={owner.truckownerzip} onChange={o('truckownerzip')} readOnly={ownerLocked} />
            <TextField label="Owner Cell" value={owner.truckownercell} onChange={o('truckownercell')} readOnly={ownerLocked} type="tel" />
            <TextField label="Owner Phone" value={owner.truckownerphone} onChange={o('truckownerphone')} readOnly={ownerLocked} type="tel" />
            <TextField label="Owner Email" value={owner.truckowneremail} onChange={o('truckowneremail')} readOnly={ownerLocked} type="email" className="col-span-2" />
          </Section>
          {gridsReady ? (
            <UnitChildGrid
              title="Overweight Permits"
              rows={childRows.owpermits ?? []}
              onRowsChange={(rows) => onChildRowsChange('owpermits', rows)}
              columns={[
                { key: 'owpermittype', label: 'OW Permit Type', type: 'select', options: gridOptions(lookups.owpermittypes), className: 'min-w-[9rem]' },
                { key: 'owpermitdate', label: 'OW Permit Date', type: 'date' },
                { key: 'owpermitexpdate', label: 'OW Permit Exp', type: 'date' },
                { key: 'owpermitnum', label: 'OW Permit #', className: 'min-w-[6rem]' },
              ]}
              newRow={() => newChildRow(CHILD_FIELDS.owpermits)}
              addLabel="Add OW permit"
              removeLabel="Remove OW permit"
              emptyText="No overweight permits."
              errors={rowErrors.owpermits}
            />
          ) : <ChildrenStatus title="Overweight Permits" loading={childrenLoading} error={childrenError} />}
        </div>

        {/* ---- Column 2: lienholder / inspections / IFTA / ELD ---- */}
        <div className="space-y-3">
          <Section title="Lienholder">
            <LookupSelectField label="Lienholder" value={truck.lienholderrecno} onChange={onLienholder} options={lookupOptions(L.lienholders)} search className="col-span-2" />
            <YesNoField label="Title Loan" value={truck.titleloan} onChange={t('titleloan')} />
            <TextField label="Lien Phone" value={truck.lienphone} onChange={t('lienphone')} type="tel" />
            <TextField label="Lien Address 1" value={truck.lienadd1} onChange={t('lienadd1')} className="col-span-2" />
            <TextField label="Lien Address 2" value={truck.lienadd2} onChange={t('lienadd2')} className="col-span-2" />
            <TextField label="Lien City" value={truck.liencity} onChange={t('liencity')} className="col-span-2" />
            <TextField label="Lien State" value={truck.lienstate} onChange={t('lienstate')} maxLength={2} />
            <TextField label="Lien Zip" value={truck.lienzip} onChange={t('lienzip')} />
          </Section>
          <Section title="Inspections">
            <DateField label="DOT Inspection Date" value={truck.dotinspecdate} onChange={t('dotinspecdate')} portalContainer={portalContainer} />
            <DateField label="DOT Inspection Exp" value={truck.dotinspecexpdate} onChange={t('dotinspecexpdate')} portalContainer={portalContainer} />
            <DateField label="Registration Date" value={truck.registrationdate} onChange={t('registrationdate')} portalContainer={portalContainer} />
            <DateField label="Registration Exp" value={truck.registrationexpdate} onChange={t('registrationexpdate')} portalContainer={portalContainer} />
            <DateField label="Company Inspection Date" value={truck.companyinspecdate} onChange={t('companyinspecdate')} portalContainer={portalContainer} />
            <DateField label="Company Inspection Exp" value={truck.companyinspecexpdate} onChange={t('companyinspecexpdate')} portalContainer={portalContainer} />
          </Section>
          {!isMule && (
            <Section title="IFTA">
              <TextField label="IFTA Cert #" value={truck.iftacertnum} onChange={t('iftacertnum')} />
              <DateField label="IFTA Exp Date" value={truck.iftaexpdate} onChange={t('iftaexpdate')} portalContainer={portalContainer} />
            </Section>
          )}
          {gridsReady ? (
            <UnitChildGrid
              title="ELD"
              rows={childRows.eld ?? []}
              onRowsChange={(rows) => onChildRowsChange('eld', rows)}
              columns={[
                { key: 'eldtype', label: 'ELD Type', type: 'select', options: gridOptions(lookups.eldtypes) },
                { key: 'eldreferencenum', label: 'ELD Reference', className: 'min-w-[7rem]' },
                { key: 'eldstartdate', label: 'ELD Start', type: 'date' },
                { key: 'eldenddate', label: 'ELD End', type: 'date' },
              ]}
              newRow={() => newChildRow(CHILD_FIELDS.eld, { eldtype: 'ELD' })}
              addLabel="Add ELD"
              removeLabel="Remove ELD"
              emptyText="No ELD / GPS / camera devices."
              errors={rowErrors.eld}
              rowClass={(r) => (isCurrentRow(r.eldstartdate, r.eldenddate) ? 'bg-highlight' : '')}
            />
          ) : <ChildrenStatus title="ELD" loading={childrenLoading} error={childrenError} />}
        </div>

        {/* ---- Column 3: photo + truck ---- */}
        <div className="space-y-3">
          <div className="flex items-center justify-center rounded-md border border-rule bg-highlight p-2">
            {photoUrl ? (
              <img src={photoUrl} alt="Truck photo" className="max-h-48 rounded-md object-contain" />
            ) : (
              <div className="flex h-32 w-full items-center justify-center text-sm text-muted">No truck photo on file</div>
            )}
          </div>
          <Section title="Truck">
            <TextField label="Truck Type" value={truck.trucktype} onChange={t('trucktype')} />
            <YesNoField label="Day Cab" value={truck.daycab} onChange={t('daycab')} />
            <TextField label="VIN" value={truck.truckvin} onChange={t('truckvin')} className="col-span-2" />
            <TextField label="Year" value={truck.truckyear} onChange={t('truckyear')} maxLength={4} />
            <TextField label="Color" value={truck.truckcolor} onChange={t('truckcolor')} />
            <LookupSelectField label="Make" value={truck.truckmake} onChange={t('truckmake')} options={lookupOptions(L.truckmakes)} search />
            <TextField label="Model" value={truck.truckmodel} onChange={t('truckmodel')} />
            <TextField label="Weight" value={truck.truckweight} onChange={t('truckweight')} />
            <MoneyField label="Coverage / Market Value" value={truck.cmv} onChange={t('cmv')} />
            <LookupSelectField label="Tire Size" value={truck.trucktiresize} onChange={t('trucktiresize')} options={lookupOptions(L.tiresizes)} search />
            <TextField label="Wheelbase" value={truck.truckwheelbase} onChange={t('truckwheelbase')} />
            <TextField label="License Plate" value={truck.trucklicplate} onChange={t('trucklicplate')} />
            <TextField label="Plate State" value={truck.trucklicstate} onChange={t('trucklicstate')} maxLength={2} />
            <TextField label="Plate Type" value={truck.trucklictype} onChange={t('trucklictype')} />
            <TextField label="Blackberry ID" value={truck.blackberryid} onChange={t('blackberryid')} />
          </Section>
        </div>
      </div>
    </PermissionAccessProvider>
  );
}
